import React from "react";
import { useEffect, useState } from "react";
import "./BillDetail.css";
import BillOverview from "../../components/BillOverview/BillOverview";
import BillDetailSkeleton from "../../components/BillDetailSkeleton/BillDetailSkeleton";
import StickyMobileCTA from "../../components/StickyMobileCTA/StickyMobileCTA";
import ContactModal from "../../components/ContactModal/ContactModal";
import { Box } from "@mui/joy";
import SEOHead from "../../components/SEOHead/SEOHead";
import seoConfig from "../../utils/seoConfig";

const ARRAY_FIELDS = ["whoItAffects", "tags", "cosponsors", "actions", "relatedBills"];

function parseJsonField(val) {
  if (Array.isArray(val)) return val;
  if (typeof val === "string") {
    try { return JSON.parse(val); } catch { return []; }
  }
  return [];
}

function normalizeBill(bill) {
  if (!bill) return bill;
  const result = { ...bill };
  for (const field of ARRAY_FIELDS) {
    if (field in result) result[field] = parseJsonField(result[field]);
  }
  return result;
}

function BillDetail() {
  const [loading, setLoading] = useState(false);
  const [bill, setBill] = useState(null);
  const [error, setError] = useState(false);
  const [open, setOpen] = useState(false);
  const params = new URLSearchParams(window.location.search);
  const currentPath = window.location.search;

  useEffect(() => {
    setLoading(true);
    const cached = localStorage.getItem(currentPath);
    const cachedBill = cached ? JSON.parse(cached) : null;

    if (cachedBill?.description) {
      setBill(normalizeBill(cachedBill));
      setLoading(false);
      document.title = `${cachedBill.title} | Civic Engage`;
    } else {
      fetch(
        `/api/bill/${params.get("congress")}/${params.get("type")}/${params.get("billIdNumber")}`
      )
        .then((response) => {
          if (!response.ok) throw new Error(`HTTP ${response.status}`);
          return response.json();
        })
        .then((data) => {
          const normalized = normalizeBill(data);
          setBill(normalized);
          localStorage.setItem(currentPath, JSON.stringify(normalized));
          document.title = `${data.title} | Civic Engage`;
          setLoading(false);
        })
        .catch(() => {
          setError(true);
          setLoading(false);
        });
    }
  }, []);

  return (
    <>
      {bill && (
        <SEOHead
          title={`${bill.title} | Civic Engage`}
          description={bill.ai_description || bill.summary || `Track ${bill.bill_id} - ${bill.title}`}
          keywords={`${bill.bill_id}, ${bill.type}, federal bill, congress legislation, ${bill.policy_area || ""}`}
          path={`/bill?congress=${bill.congress}&type=${bill.type}&billIdNumber=${bill.bill_id_number}`}
          structuredData={[
            seoConfig.getArticleSchema(bill),
            seoConfig.getBreadcrumbs(
              `/bill?congress=${bill.congress}&type=${bill.type}&billIdNumber=${bill.bill_id_number}`,
              bill.title
            ),
          ]}
        />
      )}

      <Box sx={{ backgroundColor: "white" }}>
        {loading && bill === null ? (
          <BillDetailSkeleton />
        ) : error ? (
          <Box sx={{ maxWidth: "600px", mx: "auto", py: "80px", px: "24px", textAlign: "center" }}>
            <Box component="p" sx={{ fontSize: "18px", color: "#32383e", mb: "24px" }}>
              This bill couldn&apos;t be loaded. It may have moved or the link may be incorrect.
            </Box>
            <Box
              component="a"
              href="/bills"
              sx={{ color: "#0075b2", fontWeight: 600, fontSize: "16px", textDecoration: "underline" }}
            >
              Browse all bills
            </Box>
          </Box>
        ) : (
          <>
            {bill && <BillOverview bill={bill} setOpen={setOpen} />}
            <ContactModal open={open} setOpen={setOpen} bill={bill} />
            <StickyMobileCTA setOpen={setOpen} />
          </>
        )}
      </Box>
    </>
  );
}

export default BillDetail;
